import { createContext, ReactNode, Suspense, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Bell, ChevronDown, HelpCircle, Menu, PanelLeft, Search } from "lucide-react";
import { toast } from "sonner";
import { AppSurface } from "@/components/app/AppSurface";
import { ConfirmProvider } from "@/components/app/kit/ConfirmDialog";
import { AppLogo } from "@/components/app/brand/AppLogo";
import { ProfileDropdown } from "@/components/ProfileDropdown";
import { AuthDebugPanel } from "@/components/dev/AuthDebugPanel";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useAuth } from "@/contexts/AuthContext";
import { AppRole, defaultDashboardFor } from "@/config/roles";
import { getNavigationForRole } from "@/config/navigation";
import { shellNavFor, ShellLink } from "@/config/shell-nav";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

const RetainContext = createContext<(() => () => void) | null>(null);
const SlotContext = createContext<HTMLElement | null>(null);

const BOTTOM: Record<string, [string, string][]> = {
  fan: [["/dashboard", "home"], ["/explore", "explore"], ["/fan/events", "events"], ["/social", "social"]],
  player: [["/dashboard", "home"], ["/player/schedule", "schedule"], ["/player/individual-training", "plans"], ["/player/my-profile", "me"]],
  parent: [["/dashboard", "home"], ["/children", "children"], ["/children/schedule", "schedule"], ["/children/program-fee", "fees"]],
  coach: [["/dashboard", "home"], ["/coach/schedule", "schedule"], ["/club/players", "players"], ["/coach/welfare", "welfare"]],
  club_owner: [["/dashboard", "home"], ["/club/players", "players"], ["/club/schedule", "schedule"], ["/club/player-payments", "payments"]],
  club_admin: [["/dashboard", "home"], ["/club/applications", "applications"], ["/club/players", "players"], ["/club/staff", "staff"]],
  club_manager: [["/dashboard", "home"], ["/club/schedule", "schedule"], ["/club/programs", "programmes"], ["/club/posting", "posts"]],
  club_finance: [["/dashboard", "home"], ["/club/player-payments", "payments"], ["/club/programs", "programmes"], ["/club/players", "players"]],
  admin: [["/dashboard", "home"], ["/admin/approval-center", "approvals"], ["/admin/user-management", "users"], ["/admin/moderation", "moderation"]],
  super_admin: [["/dashboard", "home"], ["/admin/approval-center", "approvals"], ["/super/subscriptions/dashboard", "subscriptions"], ["/admin/system-reports", "reports"]],
  venue_owner: [["/dashboard", "home"], ["/venue/bookings", "bookings"], ["/venue/my-venue", "explore"], ["/venue/pricing", "pricing"]],
  tournament_organizer: [["/dashboard", "home"], ["/organizer/tournament", "tournaments"], ["/organizer/schedule", "schedule"], ["/organizer/tournament/participants", "applications"]],
  team_manager: [["/team-manager/dashboard", "home"], ["/club/players", "players"], ["/team-manager/match-coordination", "logistics"], ["/team-manager/welfare", "welfare"]],
  scout: [["/dashboard", "home"], ["/explore", "explore"], ["/scout/reports", "reports"], ["/scout/trial-tracking", "schedule"]],
  scouter: [["/dashboard", "home"], ["/explore", "explore"], ["/scouter/contracts", "applications"], ["/scouter/performance", "reports"]],
  referee: [["/referee/assignments", "schedule"], ["/explore", "explore"], ["/referee/result-entry", "reports"], ["/favorites", "me"]],
  medical_staff: [["/medical/welfare", "welfare"], ["/explore", "explore"], ["/medical/injuries", "players"], ["/favorites", "me"]],
};

function useMinWidth(px: number) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const query = window.matchMedia(`(min-width: ${px}px)`);
    const apply = () => setMatches(query.matches);
    apply();
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, [px]);
  return matches;
}

function roleName(role: string) {
  return role.split("_").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
}

function groupsFor(role: string | null) {
  const fromSidebar = shellNavFor(role);
  if (fromSidebar.length > 0) return fromSidebar;
  if (!role) return [];
  return [{
    title: "Menu",
    items: getNavigationForRole(role as AppRole).map((item) => ({
      path: item.path,
      label: item.label,
      hidden: item.isUnderConstruction,
    })),
  }];
}

function visibleItems(role: string | null): ShellLink[] {
  const showHidden = import.meta.env.DEV;
  return groupsFor(role).flatMap((group) => group.items).filter((item) => showHidden || !item.hidden);
}

export function ShellProvider({ children }: { children: ReactNode }) {
  const holders = useRef(0);
  const slotNode = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const onSlot = useCallback((node: HTMLElement | null) => {
    if (slotNode.current === node) return;
    slotNode.current = node;
    setSlot(node);
  }, []);
  const retain = useCallback(() => {
    holders.current += 1;
    setOpen(true);
    return () => {
      holders.current -= 1;
      queueMicrotask(() => {
        if (holders.current === 0) setOpen(false);
      });
    };
  }, []);

  return (
    <ConfirmProvider>
      <RetainContext.Provider value={retain}>
        <SlotContext.Provider value={slot}>
          {children}
          {open ? <AppShell onSlot={onSlot} /> : null}
        </SlotContext.Provider>
      </RetainContext.Provider>
    </ConfirmProvider>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const retain = useContext(RetainContext);
  const slot = useContext(SlotContext);
  const location = useLocation();
  useEffect(() => {
    try {
      const raw = localStorage.getItem("fanjago.recent");
      const recent = raw ? (JSON.parse(raw) as string[]) : [];
      const next = [location.pathname, ...recent.filter((path) => path !== location.pathname)].slice(0, 5);
      localStorage.setItem("fanjago.recent", JSON.stringify(next));
    } catch {
      localStorage.removeItem("fanjago.recent");
    }
  }, [location.pathname]);
  useLayoutEffect(() => retain?.(), [retain]);
  if (!slot) return null;
  // Lazy pages must suspend inside the shell. The app-level Suspense sits above
  // this tree; letting a page suspend there hides the shell, detaches the main
  // slot, and the slot's setState reveals the shell again. Radix menus in the
  // header replay that hide/reveal until React hits its update limit.
  return createPortal(
    <Suspense fallback={<PageFallback />}>
      <div key={location.pathname} className="app-page-in">{children}</div>
    </Suspense>,
    slot,
  );
}

function PageFallback() {
  const { t } = useTranslation();
  return <div role="status" className="p-6 text-sm text-muted-foreground">{t("kit.loading")}</div>;
}

function AppShell({ onSlot }: { onSlot: (node: HTMLElement | null) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { roles, activeRole, switchRole, clubs, activeClub, switchClub, user } = useAuth();
  const desktop = useMinWidth(1024);
  const tablet = useMinWidth(768);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("fanjago.sidebar") === "rail");
  const [scrolled, setScrolled] = useState(false);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [keyboard, setKeyboard] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [tab, setTab] = useState<"all" | "training" | "announcements">("all");
  const rail = tablet && (!desktop || collapsed);
  const items = visibleItems(activeRole);
  const path = location.pathname.replace(/\/$/, "") || "/";
  const aliased = path === "/coach/dashboard" ? "/dashboard" : path;
  const current = items.find((item) => item.path === aliased);
  const subPage = !current && path !== "/dashboard";
  const hideBottom = !tablet && (keyboard || /tactical-board|checkout|scoring/.test(location.pathname));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const viewport = window.visualViewport;
    const onViewport = () => setKeyboard(Boolean(viewport && window.innerHeight - viewport.height > 150));
    viewport?.addEventListener("resize", onViewport);
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = Boolean(target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable));
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen(true);
      } else if (event.key === "/" && !typing) {
        event.preventDefault();
        setCommandOpen(true);
      } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "b" && desktop) {
        event.preventDefault();
        setCollapsed((value) => {
          localStorage.setItem("fanjago.sidebar", value ? "expanded" : "rail");
          return !value;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      viewport?.removeEventListener("resize", onViewport);
      window.removeEventListener("keydown", onKey);
    };
  }, [desktop]);

  const notes = useQuery({
    queryKey: ["shell-notes", user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data, error } = await supabase.from("in_app_notifications").select("id, title, message, created_at, read_at, action_url").eq("user_id", user!.id).order("created_at", { ascending: false }).limit(30);
      if (error) throw error;
      return data ?? [];
    },
  });
  const announcements = useQuery({
    queryKey: ["shell-announcements", user?.id],
    enabled: notesOpen,
    queryFn: async () => {
      const { data, error } = await supabase.from("club_announcements").select("id, title, content, created_at").order("created_at", { ascending: false }).limit(12);
      if (error) throw error;
      return data ?? [];
    },
  });
  const training = useQuery({
    queryKey: ["shell-training", user?.id, activeRole],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      const { count, error } = await supabase.from("trainings").select("id", { count: "exact", head: true }).gte("training_date", start.toISOString()).lte("training_date", end.toISOString());
      if (error) throw error;
      return count ?? 0;
    },
  });

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase.channel(`shell-notes-${user.id}`).on("postgres_changes", { event: "*", schema: "public", table: "in_app_notifications", filter: `user_id=eq.${user.id}` }, () => {
      queryClient.invalidateQueries({ queryKey: ["shell-notes", user.id] });
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, user?.id]);

  const unread = (notes.data ?? []).filter((item) => !item.read_at).length + (training.data ?? 0);
  let recent: string[] = [];
  if (commandOpen) {
    try {
      recent = JSON.parse(localStorage.getItem("fanjago.recent") ?? "[]") as string[];
    } catch {
      recent = [];
    }
  }

  const changeRole = async (role: AppRole) => {
    const previous = activeRole;
    if (user) {
      await supabase.from("user_roles").update({ last_used_at: new Date().toISOString() }).eq("user_id", user.id).eq("role", role);
    }
    await switchRole(role, { silent: true });
    navigate(defaultDashboardFor(roles, role));
    document.getElementById("main")?.focus();
    toast(t("shell.nowUsing", { role: roleName(role) }), {
      duration: 5000,
      action: previous ? { label: t("shell.undo"), onClick: () => { void switchRole(previous, { silent: true }); navigate(defaultDashboardFor(roles, previous)); } } : undefined,
    });
  };

  const changeClub = (clubId: string) => {
    const club = clubs.find((item) => item.id === clubId);
    const previous = activeClub;
    if (!club) return;
    switchClub(club, { silent: true });
    queryClient.invalidateQueries({ predicate: (query) => JSON.stringify(query.queryKey).toLowerCase().includes("club") });
    toast(t("shell.nowClub", { club: club.club_name }), {
      duration: 5000,
      action: previous ? { label: t("shell.undo"), onClick: () => switchClub(previous, { silent: true }) } : undefined,
    });
  };

  const markAll = async () => {
    if (!user) return;
    const ids = (notes.data ?? []).filter((item) => !item.read_at).map((item) => item.id);
    await supabase.from("in_app_notifications").update({ read_at: new Date().toISOString() }).in("id", ids);
    queryClient.invalidateQueries({ queryKey: ["shell-notes", user.id] });
    toast(t("shell.markRead"), {
      action: { label: t("shell.undo"), onClick: async () => {
        await supabase.from("in_app_notifications").update({ read_at: null }).in("id", ids);
        queryClient.invalidateQueries({ queryKey: ["shell-notes", user.id] });
      } },
    });
  };

  useEffect(() => {
    document.title = current?.label ? `${current.label} | FanJago` : "FanJago";
  }, [current?.label]);

  const tabs = BOTTOM[activeRole ?? ""] ?? [["/dashboard", "home"], ["/explore", "explore"]];
  const moreItems = items.filter((item) => !tabs.some(([path]) => path === item.path));

  return (
    <AppSurface>
      <div className="min-h-dvh bg-background text-foreground">
        <a href="#main" className="sr-only z-app-skip focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground">{t("shell.skip")}</a>
        {tablet && (
          <aside className={cn("fixed inset-y-0 left-0 z-app-bar flex flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground", rail ? "w-[72px]" : "w-[264px]")}>
            <Link to="/dashboard" className="flex h-16 items-center px-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" title="FanJago">
              <AppLogo variant={rail ? "mark" : "wordmark"} size={28} />
            </Link>
            <ContextSwitcher rail={rail} onRole={changeRole} onClub={changeClub} />
            <nav className="flex-1 space-y-4 overflow-y-auto px-2 py-3" aria-label="Primary">
              {groupsFor(activeRole).map((group) => (
                <div key={group.title}>
                  {!rail && <p className="px-3 pb-1 text-xs uppercase tracking-wide text-muted-foreground">{group.title}</p>}
                  <ul className="space-y-1">
                    {group.items.filter((item) => import.meta.env.DEV || !item.hidden).map((item) => {
                      const active = location.pathname === item.path;
                      return (
                        <li key={item.path}>
                          <Link to={item.path} title={item.label} aria-current={active ? "page" : undefined} className={cn("flex min-h-11 items-center gap-2 rounded-md px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", active ? "border-l-[3px] border-primary bg-sidebar-accent font-medium text-primary" : "text-sidebar-foreground")}>
                            {!rail && <span className="truncate">{item.label}</span>}
                            {rail && <span className="sr-only">{item.label}</span>}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>
            <div className="space-y-1 border-t border-sidebar-border p-2">
              <Link to="/support" className="flex min-h-11 items-center gap-2 rounded-md px-3 text-sm"><HelpCircle className="h-4 w-4" aria-hidden />{!rail && t("shell.help")}</Link>
              <Link to="/profile" className="flex min-h-11 items-center gap-2 rounded-md px-3 text-sm">{!rail && t("shell.settings")}</Link>
              {desktop && (
                <button type="button" className="flex min-h-11 w-full items-center gap-2 rounded-md px-3 text-left text-sm" onClick={() => setCollapsed((value) => { localStorage.setItem("fanjago.sidebar", value ? "expanded" : "rail"); return !value; })}>
                  <PanelLeft className="h-4 w-4" aria-hidden />
                  {!rail && t(collapsed ? "shell.expand" : "shell.collapse")}
                </button>
              )}
            </div>
          </aside>
        )}
        <div className={cn("flex min-h-dvh flex-col", tablet && (rail ? "pl-[72px]" : "pl-[264px]"))}>
          <header className={cn("sticky top-0 z-app-bar flex h-14 items-center gap-2 border-b border-border px-3 md:h-16", scrolled && "bg-background/85 shadow-e2 backdrop-blur-md")}>
            {subPage ? (
              <button type="button" className="flex h-11 w-11 items-center justify-center md:hidden" aria-label={t("shell.back")} onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/dashboard"))}>
                <span aria-hidden>←</span>
              </button>
            ) : (
              <Link to="/dashboard" className="md:hidden" aria-label="FanJago"><AppLogo variant="mark" size={24} /></Link>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-heading text-[17px] font-semibold leading-6 md:hidden">{current?.label ?? (path === "/dashboard" ? t("shell.short.home") : t("shell.page"))}</p>
              <nav aria-label="Breadcrumb" className="hidden text-sm md:block">
                <ol className="flex items-center gap-2">
                  <li><Link to="/dashboard" className="text-muted-foreground">{t("shell.short.home")}</Link></li>
                  {current && <li aria-current="page" className="font-medium">/ {current.label}</li>}
                </ol>
              </nav>
            </div>
            <button type="button" className="hidden h-11 min-w-[280px] max-w-[480px] flex-1 items-center gap-2 rounded-md border border-border px-3 text-sm text-muted-foreground md:flex" onClick={() => setCommandOpen(true)}>
              <Search className="h-4 w-4" aria-hidden />
              {t("shell.searchHint")}
              <span className="ml-auto">⌘K</span>
            </button>
            <button type="button" className="flex h-11 w-11 items-center justify-center md:hidden" aria-label={t("shell.search")} onClick={() => setCommandOpen(true)}><Search className="h-5 w-5" /></button>
            <button type="button" className="relative flex h-11 w-11 items-center justify-center" aria-label={t("shell.unread", { count: unread })} onClick={() => setNotesOpen(true)}>
              <Bell className="h-5 w-5" />
              {unread > 0 && <span className="absolute right-1 top-1 min-w-5 rounded-full bg-primary px-1 text-xs text-primary-foreground motion-safe:animate-in">{unread > 9 ? "9+" : unread}</span>}
            </button>
            <ProfileDropdown />
          </header>
          {!online && <p role="status" className="bg-status-warning px-4 py-2 text-sm text-status-warning">{t("shell.offline")}</p>}
          <main id="main" tabIndex={-1} ref={onSlot} className={cn("flex-1 outline-none", !tablet && !hideBottom && "pb-[calc(4rem+env(safe-area-inset-bottom))]")} />
        </div>
        {!hideBottom && !tablet && (
          <nav className="fixed inset-x-0 bottom-0 z-app-bar flex border-t border-border bg-background pb-[env(safe-area-inset-bottom)]" aria-label="Primary">
            {tabs.map(([path, key]) => (
              <Link key={path} to={path} aria-current={location.pathname === path ? "page" : undefined} className={cn("flex min-h-16 flex-1 flex-col items-center justify-center text-xs", location.pathname === path ? "font-semibold text-primary" : "text-muted-foreground")}>
                {t(`shell.short.${key}`)}
              </Link>
            ))}
            <button type="button" className="flex min-h-16 flex-1 flex-col items-center justify-center text-xs" onClick={() => setMoreOpen(true)}>
              <Menu className="h-4 w-4" aria-hidden />
              {t("shell.more")}
            </button>
          </nav>
        )}
        <CommandDialog open={commandOpen} onOpenChange={setCommandOpen}>
          <CommandInput placeholder={t("shell.searchHint")} />
          <CommandList>
            <CommandEmpty>{t("shell.noMatches", { query: "" })}</CommandEmpty>
            <CommandGroup heading={t("shell.recent")}>
              {recent.map((path) => <CommandItem key={path} onSelect={() => { setCommandOpen(false); navigate(path); }}>{items.find((item) => item.path === path)?.label ?? path}</CommandItem>)}
            </CommandGroup>
            <CommandGroup heading={t("shell.goTo")}>
              {items.map((item) => <CommandItem key={item.path} value={`${item.label} ${item.path}`} onSelect={() => { setCommandOpen(false); navigate(item.path); }}>{item.label}</CommandItem>)}
            </CommandGroup>
            <CommandGroup heading={t("shell.switchRole")}>
              {roles.map((role) => <CommandItem key={role} onSelect={() => { setCommandOpen(false); void changeRole(role); }}>{roleName(role)}</CommandItem>)}
            </CommandGroup>
          </CommandList>
        </CommandDialog>
        <Sheet open={notesOpen} onOpenChange={setNotesOpen}>
          <SheetContent className="flex w-full flex-col sm:max-w-[400px]">
            <SheetHeader><SheetTitle>{t("shell.notifications")}</SheetTitle></SheetHeader>
            <div className="flex gap-2 overflow-x-auto py-2">
              {(["all", "training", "announcements"] as const).map((name) => (
                <button key={name} type="button" className={cn("h-9 rounded-full px-3 text-sm", tab === name ? "bg-primary text-primary-foreground" : "bg-muted")} onClick={() => setTab(name)}>{t(`shell.${name}`)}</button>
              ))}
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto" aria-live="polite">
              {(notes.isError || announcements.isError) && <button type="button" className="text-sm underline" onClick={() => { notes.refetch(); announcements.refetch(); }}>{t("shell.loadError")} · {t("shell.retry")}</button>}
              {tab !== "announcements" && (notes.data ?? []).length === 0 && !notes.isLoading && <p className="text-sm text-muted-foreground">{t("shell.caughtUp")}</p>}
              {tab !== "announcements" && (notes.data ?? []).filter((item) => tab === "all" || item.title.toLowerCase().includes("train")).map((item) => (
                <button key={item.id} type="button" className="block w-full rounded-md border border-border p-3 text-left" onClick={async () => {
                  await supabase.from("in_app_notifications").update({ read_at: new Date().toISOString() }).eq("id", item.id);
                  queryClient.invalidateQueries({ queryKey: ["shell-notes", user?.id] });
                  setNotesOpen(false);
                  if (item.action_url) navigate(item.action_url);
                }}>
                  <span className="block text-sm font-semibold">{item.title}</span>
                  <span className="line-clamp-2 text-sm text-muted-foreground">{item.message}</span>
                </button>
              ))}
              {tab === "training" && <p className="text-sm">{t("shell.trainingToday", { count: training.data ?? 0 })}</p>}
              {tab === "announcements" && (announcements.data ?? []).map((item) => (
                <div key={item.id} className="rounded-md border border-border p-3">
                  <p className="text-sm font-semibold">{item.title}</p>
                  <p className="line-clamp-2 text-sm text-muted-foreground">{item.content}</p>
                </div>
              ))}
            </div>
            <button type="button" className="mt-3 text-sm underline" onClick={() => void markAll()}>{t("shell.markRead")}</button>
          </SheetContent>
        </Sheet>
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetContent side="bottom" className="max-h-[70dvh] overflow-y-auto">
            <SheetHeader><SheetTitle>{t("shell.more")}</SheetTitle></SheetHeader>
            <ul className="space-y-1 py-3">
              {moreItems.map((item) => (
                <li key={item.path}><Link to={item.path} className="flex min-h-11 items-center px-2" onClick={() => setMoreOpen(false)}>{item.label}</Link></li>
              ))}
              <li className="border-t pt-1"><Link to="/support" className="flex min-h-11 items-center gap-2 px-2" onClick={() => setMoreOpen(false)}><HelpCircle className="h-4 w-4" aria-hidden />{t("shell.help")}</Link></li>
              <li><Link to="/profile" className="flex min-h-11 items-center px-2" onClick={() => setMoreOpen(false)}>{t("shell.settings")}</Link></li>
            </ul>
          </SheetContent>
        </Sheet>
        {import.meta.env.DEV && <div className="fixed bottom-20 right-3 z-app-toast"><AuthDebugPanel /></div>}
      </div>
    </AppSurface>
  );
}

function ContextSwitcher({ rail, onRole, onClub }: { rail: boolean; onRole: (role: AppRole) => void; onClub: (id: string) => void }) {
  const { t } = useTranslation();
  const { roles, activeRole, clubs, activeClub } = useAuth();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="mx-2 flex min-h-11 items-center gap-2 rounded-md px-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {!rail && (
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">{activeRole ? roleName(activeRole) : t("shell.roles")}</span>
              <span className="block truncate text-xs text-muted-foreground">{activeClub?.club_name ?? t("shell.clubs")}</span>
            </span>
          )}
          <ChevronDown className="h-4 w-4" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <p className="px-2 py-1 text-xs text-muted-foreground">{t("shell.roles")}</p>
        {roles.map((role) => (
          <button key={role} type="button" className="flex min-h-11 w-full items-center rounded-md px-2 text-left text-sm" onClick={() => onRole(role)}>{roleName(role)}</button>
        ))}
        {clubs.length > 0 && <p className="px-2 py-1 text-xs text-muted-foreground">{t("shell.clubs")}</p>}
        {clubs.map((club) => (
          <button key={club.id} type="button" className="flex min-h-11 w-full items-center rounded-md px-2 text-left text-sm" onClick={() => onClub(club.id)}>{club.club_name}</button>
        ))}
        <Link to="/role-request" className="flex min-h-11 items-center px-2 text-sm">{t("shell.requestRole")}</Link>
      </PopoverContent>
    </Popover>
  );
}
